import i18n, { LANGUE_PAR_DEFAUT } from '.';

/* Tables de libellés déclarées au chargement d'un module (statuts, types, options
   de listes), traduites au moment où on les LIT.

   Une constante `{ NOUVEAU: 'Nouveau' }` garderait la langue du démarrage, et la
   transformer en fonction obligerait à modifier chaque endroit qui s'en sert. Ces
   aides gardent la même forme (objet, tableau d'options) : `STATUTS[code]`,
   `Object.entries(STATUTS)`, `options={TYPES}` continuent de marcher, et rendent
   le texte de la langue courante.

   Les textes vivent dans les fichiers de traduction, sous `chemin` :
   `'referentiels:statutsSinistre'` → locales/<langue>/referentiels.json,
   objet "statutsSinistre", une clé par code. */

function decouper(chemin) {
  const [ns, cle] = chemin.includes(':') ? chemin.split(':') : ['commun', chemin];
  return { ns, cle };
}

// Les codes connus sont ceux du français, langue de référence et de repli.
function codes(chemin) {
  const { ns, cle } = decouper(chemin);
  const bloc = cle.split('.').reduce((o, k) => (o ? o[k] : undefined), i18n.getResourceBundle(LANGUE_PAR_DEFAUT, ns));
  return bloc && typeof bloc === 'object' ? bloc : {};
}

const traduire = (chemin, code) => {
  const { ns, cle } = decouper(chemin);
  return i18n.t(`${ns}:${cle}.${code}`);
};

/** Table code → libellé : `libellesTraduits('referentiels:statutsSinistre').NOUVEAU`. */
export function libellesTraduits(chemin) {
  const connu = (code) => typeof code === 'string' && Object.prototype.hasOwnProperty.call(codes(chemin), code);
  return new Proxy({}, {
    get: (_, code) => (connu(code) ? traduire(chemin, code) : undefined),
    has: (_, code) => connu(code),
    ownKeys: () => Object.keys(codes(chemin)),
    getOwnPropertyDescriptor: (_, code) => (connu(code)
      ? { value: traduire(chemin, code), enumerable: true, configurable: true, writable: false }
      : undefined),
  });
}

/**
 * Pose un libellé traduit, en lecture, sur chaque élément d'un tableau d'options ou
 * d'un objet de configuration. Le reste de l'élément (couleur, icône...) ne bouge pas.
 *
 *   const NIVEAUX = avecLibelleTraduit([{ value: 'CRITIQUE', color: 'red' }], 'delais:niveaux');
 *   const STATUTS = avecLibelleTraduit({ NOUVEAU: { color: 'blue' } }, 'referentiels:statuts');
 *
 * Tableau : la clé de traduction est `element[cle]` ('value' par défaut). Objet : le nom
 * de la propriété. `champ` : propriété qui porte le libellé ('label' par défaut).
 */
export function avecLibelleTraduit(elements, chemin, { cle = 'value', champ = 'label' } = {}) {
  const poser = (element, code) => {
    Object.defineProperty(element, champ, {
      get: () => traduire(chemin, code),
      enumerable: true,
      configurable: true,
    });
  };
  if (Array.isArray(elements)) elements.forEach((e) => poser(e, e[cle]));
  else Object.entries(elements).forEach(([code, e]) => poser(e, code));
  return elements;
}
